'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { LocationCtaLink } from '@/components/LocationCtaLink';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { substituteHrefChain } from '@/lib/render/substituteHref';
import type { CtaAction } from '@/types/ctaAction';

export type HeroCardCtaLayout = 'stacked' | 'inline';

interface HeroCardCtasProps {
  actions: CtaAction[];
  /** Card-level resolved phone (location → domain fallback), see 70.07. */
  fallbackPhone?: string;
  /** Card-level resolved Matelso label, hard-paired with `fallbackPhone`. */
  fallbackMatelsoPhoneLabel?: string;
  /**
   * `stacked` (default) is every card but the Home card's tab 1, whose two
   * CTAs sit side by side from `sm` (SH-312 O6).
   */
  layout?: HeroCardCtaLayout;
}

/**
 * The Hero Cards' CTA renderer.
 *
 * Ported from `components/CtaActionButton.tsx` rather than shared, because the
 * hero cards need a leading phone glyph on phone CTAs — an explicit deviation
 * from the CTA Block's OOS list ("Icon support on action buttons", see
 * 10.03.03.19-CTA-Block AC 6) that the shared button must not inherit. The
 * link/phone branching, the hard-pair rule and the label precedence are kept
 * identical on purpose; see 70.07-Phone-Number-Resolution.
 */
export function HeroCardCtas({
  actions,
  fallbackPhone,
  fallbackMatelsoPhoneLabel,
  layout = 'stacked',
}: HeroCardCtasProps) {
  const { substitutePlaceholders: sub } = useRenderContext();

  if (actions.length === 0) {
    // Zero CTAs renders no CTA area at all — no empty box and no trailing gap.
    return null;
  }

  return (
    <div
      className={classNames(
        'flex w-full gap-3',
        layout === 'inline' ? 'flex-col sm:flex-row' : 'flex-col',
      )}
    >
      {actions.map((action, i) => (
        <HeroCardCta
          key={i}
          action={action}
          fallbackPhone={fallbackPhone}
          fallbackMatelsoPhoneLabel={fallbackMatelsoPhoneLabel}
          sub={sub}
        />
      ))}
    </div>
  );
}

function HeroCardCta({
  action,
  fallbackPhone,
  fallbackMatelsoPhoneLabel,
  sub,
}: {
  action: CtaAction;
  fallbackPhone?: string;
  fallbackMatelsoPhoneLabel?: string;
  sub: (template: string) => string;
}) {
  if (action.type === 'location') {
    return <LocationCtaLink action={action} />;
  }

  const label = action.label ? sub(action.label) : undefined;
  const variant = action.type === 'secondary' ? 'secondary' : 'primary';
  // Inside the narrow hero panel the buttons are always full width; `md` keeps
  // the horizontal padding from overflowing at 360px.
  const className = 'w-full min-w-0 px-4! text-base';

  // Link action: an explicit link wins, whatever else is set. Any scheme the
  // sanitizer allows works here, so a `mailto:` CTA needs no special field.
  if (action.link) {
    // An explicit link whose placeholder did not resolve renders nothing — it
    // must not fall through to the phone branch below and dial instead. A
    // `|`-separated chain picks its first resolvable candidate first (SH-524).
    const link = substituteHrefChain(action.link, sub);
    if (!link) {
      return null;
    }
    return (
      <Button
        href={link}
        variant={variant}
        size="md"
        className={className}
        onClick={() =>
          pushCmsEventTracking(action.tracking, { label: label || link })
        }
      >
        {label || link}
      </Button>
    );
  }

  // Phone action: `(phone, matelsoPhoneLabel)` is a hard pair — an action that
  // sets its own phone uses its own label or none, never the card-level one.
  // Only an action with no phone at all falls back to the card-level pair.
  // The card-level fallbacks need `sub` just as much as the action's own
  // fields: they arrive from the CMS through `resolveHeroCardText`, whose
  // `text()` only defaults `undefined` to `''` and substitutes nothing. Without
  // this a card-level `{location.matelsoPhoneLabel}` reaches the visitor
  // verbatim while the identical token on an action's own field resolves.
  const subText = (value: string | undefined): string | undefined =>
    value ? sub(value) : undefined;
  const phone = action.phone ? sub(action.phone) : subText(fallbackPhone);
  if (!phone) {
    return null;
  }
  const matelsoPhoneLabel = action.phone
    ? subText(action.matelsoPhoneLabel)
    : subText(fallbackMatelsoPhoneLabel);
  const displayLabel = label || matelsoPhoneLabel || phone;

  return (
    <Button
      href={`tel:${phone.replace(/[^+\d]/g, '')}`}
      variant={variant}
      size="md"
      className={className}
      onClick={() =>
        pushCmsEventTracking(action.tracking, { label: displayLabel, phone })
      }
      aria-label={`Anrufen: ${displayLabel}`}
    >
      <Icon src="/icons/phone.svg" size={16} className="shrink-0" />
      <span className="truncate">{displayLabel}</span>
    </Button>
  );
}
